import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { RcraHsmLandBasedUnit, RcraHsmNotificationReason } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { selectItemsFromLabels } from "routes/Customer/Facility/ProfileV2/rcraProfileLabels";

import { HSM_LAND_BASED_UNIT_LABELS, HSM_REASON_LABELS } from "../../rcraNotificationLabels";
import { SubRecordDialog, SubRecordGrid } from "../subRecordListEditor";
import { SubRecordRow, useSubRecordEditor } from "../useSubRecordEditor";
import { emptyHsmActivity } from "./buildInputs";
import { DATE_PICKER_PROPS, gridSx, YES_NO_OPTIONS } from "./constants";
import { validateOptionalPositiveWholeNumber } from "./fieldRules";
import { SectionHeader } from "./SectionHeader";
import { FilingNotificationsFormData, HsmActivityForm } from "./types";
import { WasteCodesField } from "./WasteCodesField";
import { getInvalidHsmWasteCodes } from "./wasteCodeValidation";

/** §16 HSM Notification. */
export function HsmNotificationSection() {
  const { control, metadata } = useValidatingFormContext<FilingNotificationsFormData>();
  const isDisplayOnly = metadata?.isDisplayOnly ?? false;
  const { watch, getValues } = useFormContext<FilingNotificationsFormData>();
  const gate = watch("hsmGate");

  // §16 waste codes are a mixed federal/state list. The facility state unlocks
  // that state's wildcards (e.g. TX facility-specific codes) during validation;
  // RCRA stepper submissions are single-facility.
  const { submission } = useWasteStepper();
  const facilityState = submission?.facilitySubmissions?.[0]?.facility?.state ?? null;
  const getInvalidCodes = useCallback(
    (codes: string[]) => getInvalidHsmWasteCodes(codes, facilityState),
    [facilityState]
  );

  // Row-level flag for the grid: an error icon whose tooltip names the bad
  // codes; the dialog's red chips carry the per-code detail.
  const getRowIssue = useCallback(
    (row: HsmActivityForm) => {
      const invalid = getInvalidCodes(row.wasteCodes ?? []);
      if (invalid.length === 0) {
        return null;
      }
      return `Invalid waste code(s): ${invalid.map(({ code }) => code).join(", ")}`;
    },
    [getInvalidCodes]
  );

  const activities = watch("activities") ?? [];
  const { append, remove, update } = useFieldArray<FilingNotificationsFormData, "activities">({
    control,
    name: "activities",
  });

  const editor = useSubRecordEditor<HsmActivityForm>({
    items: activities,
    append: (value) => append(value),
    update: (index, value) => update(index, value),
    remove,
    getItem: (index) => getValues(`activities.${index}`),
  });

  const columns: GridColDef<SubRecordRow<HsmActivityForm>>[] = [
    {
      field: "facilityCode",
      headerName: "Facility Code",
      flex: 1,
      valueGetter: (_, row) => row.facilityCode ?? "",
    },
    {
      field: "estimatedShortTons",
      headerName: "Estimated Short Tons",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => row.estimatedShortTons ?? "",
    },
    {
      field: "landBasedUnit",
      headerName: "Land-Based Unit",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) =>
        row.landBasedUnit ? HSM_LAND_BASED_UNIT_LABELS[row.landBasedUnit] : "",
    },
    {
      field: "wasteCodes",
      headerName: "Waste Codes",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => (row.wasteCodes ?? []).join(", "),
    },
  ];

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="HSM Notification (§16)" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Are you submitting an HSM notification this filing?"
          name="hsmGate"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      {gate === true && (
        <>
          <Box sx={{ gridColumn: "span 12" }}>
            <RadioGroupField
              label="Is this site managing hazardous secondary material?"
              name="isManagingHsm"
              control={control}
              radioOptions={YES_NO_OPTIONS}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormSelect
              label="Reason"
              name="reason"
              control={control}
              selectItems={selectItemsFromLabels<RcraHsmNotificationReason>(HSM_REASON_LABELS)}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <DateField
              label="Effective date"
              name="effectiveDate"
              control={control}
              datePickerProps={DATE_PICKER_PROPS}
            />
          </Box>
          <SubRecordGrid<HsmActivityForm>
            rows={editor.rows}
            dataColumns={columns}
            listLabel="HSM activities"
            addLabel="Add HSM activity"
            isDisplayOnly={isDisplayOnly}
            onAdd={editor.openAdd}
            onEdit={editor.openEdit}
            rowToDelete={editor.rowToDelete}
            onRequestDelete={editor.requestDelete}
            onConfirmDelete={editor.confirmDelete}
            deleteHeader="Remove this HSM activity?"
            renderDeleteLabel={(row) => row.facilityCode ?? "Unnamed activity"}
            getRowIssue={getRowIssue}
          />
        </>
      )}
      {editor.isOpen && (
        <SubRecordDialog<HsmActivityForm>
          title={editor.isAdding ? "Add HSM activity" : "Edit HSM activity"}
          gridSx={gridSx}
          initialValues={editor.editingValues ?? emptyHsmActivity()}
          isDisplayOnly={isDisplayOnly}
          onCancel={editor.close}
          onCommit={editor.commit}
          renderFields={(dialogForm) => (
            <>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <FormTextField
                  name="facilityCode"
                  label="Facility code"
                  control={dialogForm.control}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <FormTextField
                  name="estimatedShortTons"
                  label="Estimated short tons"
                  control={dialogForm.control}
                  rules={{ validate: validateOptionalPositiveWholeNumber }}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <FormSelect
                  label="Land-based unit"
                  name="landBasedUnit"
                  control={dialogForm.control}
                  selectItems={selectItemsFromLabels<RcraHsmLandBasedUnit>(
                    HSM_LAND_BASED_UNIT_LABELS
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <WasteCodesField
                  name="wasteCodes"
                  label="Waste codes"
                  control={dialogForm.control}
                  disabled={isDisplayOnly}
                  getInvalidCodes={getInvalidCodes}
                />
              </Box>
            </>
          )}
        />
      )}
    </Box>
  );
}
